import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { getTrips } from '../services/api';

/**
 * Bookings page component
 * @returns {JSX.Element} BookingsPage component
 */
const BookingsPage = () => {
  const navigate = useNavigate();
  const [, setBookings] = useState([]);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);

  // Add caching to prevent duplicate API calls
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [lastLoadParams, setLastLoadParams] = useState(null);
  
  // Filter states
  const [filters] = useState({
    searchQuery: '',
    bookingDateRange: { start: null, end: null },
    flightDateRange: { start: null, end: null },
    pnr: '',
    passengerName: '',
    email: '',
    bookingStatus: '',
    paymentStatus: '',
    airline: '',
    route: '',
    class: '',
    userId: '',
    createdAt: '',
    departureDate: ''
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Transform API response to booking format
  const transformTripToBooking = (trip) => {
    // Prefer explicit transaction/payment status from API if available
    const paymentStatus =
      trip.paymentStatus ||
      (trip.transaction && trip.transaction.status) ||
      (trip.status === 'PAYMENT_INITIATED'
        ? 'INITIATED'
        : trip.status === 'BOOKING_SUCCESS'
        ? 'COMPLETED'
        : trip.status === 'BOOKING_FAILED'
        ? 'FAILED'
        : 'INITIATED');

    return {
      id: trip.tripId,
      bookingDate: trip.createdAt,
      flightDate: trip.departureDate,
      flight: {
        route: `${trip.originAirport || 'N/A'}_${trip.destinationAirport || 'N/A'}`,
        class: trip.class || 'Economy'
      },
      bookingStatus: trip.status || 'BOOKING_INITIALISED',
      paymentStatus,
      amount: parseFloat(trip.totalAmount) || 0,
      currency: trip.currency || 'INR',
      pnr: trip.pnr || 'N/A',
      userId: trip.userId
    };
  };

  // Load bookings data
  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare API parameters - only include non-empty values
      const apiParams = {
        limit: pagination.limit,
        offset: pagination.page * pagination.limit
      };

      // Only add filters if they have values
      if (filters.userId) apiParams.userId = filters.userId;
      if (filters.bookingStatus) apiParams.status = filters.bookingStatus;
      if (filters.paymentStatus) apiParams.paymentStatus = filters.paymentStatus;
      if (filters.createdAt) apiParams.createdAt = filters.createdAt;
      if (filters.departureDate) apiParams.departureDate = filters.departureDate;

      // Check if we're making the same request as before
      const currentParams = JSON.stringify(apiParams);
      if (hasInitiallyLoaded && lastLoadParams === currentParams) {
        console.log('Skipping duplicate API call for bookings');
        setLoading(false);
        return;
      }

      console.log('Loading bookings with params:', apiParams);

      const response = await getTrips(apiParams);
      console.log('API Response:', response);

      if (response.success && Array.isArray(response.data)) {
        const transformedBookings = response.data.map(transformTripToBooking);

        // Client-side fallback filter when backend ignores paymentStatus
        const finalBookings = filters.paymentStatus
          ? transformedBookings.filter(b => (b.paymentStatus || '').toUpperCase() === filters.paymentStatus.toUpperCase())
          : transformedBookings;

        setBookings(finalBookings);

        setPagination(prev => ({
          ...prev,
          total: finalBookings.length,
          totalPages: Math.ceil(finalBookings.length / prev.limit)
        }));

        setLastLoadParams(currentParams);
        setHasInitiallyLoaded(true);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (err) {
      setError(`Failed to load bookings: ${err.message}`);
      console.error('Error loading bookings:', err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.userId, filters.bookingStatus, filters.paymentStatus, filters.createdAt, filters.departureDate, pagination.page, pagination.limit]);

  // Handle booking actions
  // eslint-disable-next-line no-unused-vars
  const handleBookingAction = (action, booking) => {
    // eslint-disable-next-line no-unused-vars
    console.log(`${action} action for booking:`, booking);
    switch (action) {
      case 'view':
        // Navigate to booking details
        navigate(`/bookings/${booking.id}`);
        break;
      case 'edit':
        // Open edit modal
        break;
      case 'uploadPdf':
        // Open file upload
        break;
      case 'sendEmail':
        // Send ticket email
        break;
      case 'sendWhatsApp':
        // Send ticket via WhatsApp
        break;
      case 'cancel':
        // Cancel booking
        break;
      case 'refund':
        // Process refund
        break;
      case 'markPaid':
        // Mark as paid
        break;
      default:
        break;
    }
  };

  return (
    <MainLayout>
      {/* Bookings Dashboard - TEMPORARILY DISABLED */}
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🚧</div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Bookings Dashboard</h2>
          <p className="text-gray-600">This section is temporarily unavailable.</p>
        </div>
      </div>

      {/*
      {/* Filters *\/}
      <div className="flex-shrink-0 border-b bg-gray-50 px-4 py-2">
        <BookingsFilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleFilterReset}
          loading={loading}
        />
      </div>

      {/* Content *\/}
      <div className="flex-1 min-h-0 px-4 py-2">
        {error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-2">{error}</p>
              <Button onClick={loadBookings} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        ) : (
          <BookingsTable
            bookings={bookings}
            loading={loading}
            onBookingAction={handleBookingAction}
            pagination={pagination}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        )}
      </div>
      */}
    </MainLayout>
  );
};

export default BookingsPage;
